"use client";

import { useEffect, useState } from "react";
import { ExternalLink, Newspaper } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  api,
  type MarketRelatedNewsItem,
  type MarketRelatedNewsResponse,
} from "@/lib/api";

/**
 * News Hub PR 3b — cluster summaries matched to this market by
 * keyword overlap, surfaced on /markets/[slug] above CommentsSection.
 *
 * Renders one card per cluster:
 *   - When the cluster has a published Granbay-editorial synthesis
 *     (PR 2 AiSummaryService output): show the synthesis + sentiment
 *     chip + highlighted source links.
 *   - Without a synthesis: fall back to "topArticles" (the freshest 3
 *     headlines in the cluster) so the section still renders signal.
 *
 * Hidden when the API returns 0 items — the section is decorative;
 * empty-state would clutter the page for markets without news matches.
 */
export function RelatedNewsSection({ marketSlug }: { marketSlug: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<MarketRelatedNewsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getMarketRelatedNews(marketSlug)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        // silent — section is decorative
      });
    return () => {
      cancelled = true;
    };
  }, [marketSlug]);

  if (!data || data.items.length === 0) return null;

  return (
    <section
      className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
      aria-label={t("relatedNews.ariaLabel", "Related news")}
    >
      <header className="mb-2 flex items-center gap-1.5">
        <Newspaper
          className="h-3.5 w-3.5 text-[var(--color-text3)]"
          aria-hidden
        />
        <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          {t("relatedNews.heading", "Related news")}
        </span>
        <span className="ml-auto text-[10px] text-[var(--color-text3)]">
          {data.items.length}
        </span>
      </header>
      <ul className="space-y-3">
        {data.items.map((item) => (
          <RelatedNewsItemRow key={item.clusterId} item={item} />
        ))}
      </ul>
    </section>
  );
}

function RelatedNewsItemRow({ item }: { item: MarketRelatedNewsItem }) {
  const sentimentTone =
    item.sentimentSignal === "bullish"
      ? "var(--color-yes, #2d8a5d)"
      : item.sentimentSignal === "bearish"
        ? "var(--color-no, #b34a4a)"
        : "var(--color-text3)";
  const showSynthesis = item.synthesis && item.synthesis.length > 0;

  return (
    <li className="border-l-2 border-[var(--color-border)] pl-2.5">
      <div className="mb-0.5 flex items-baseline gap-2">
        <h3 className="text-[12px] font-semibold text-[var(--color-text)]">
          {item.topic}
        </h3>
        {item.sentimentSignal && (
          <span
            className="rounded-md px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.4px]"
            style={{
              color: sentimentTone,
              borderColor: sentimentTone,
              border: "1px solid",
            }}
          >
            {item.sentimentSignal}
          </span>
        )}
        <span className="ml-auto text-[10px] text-[var(--color-text3)]">
          {item.articleCount} src
        </span>
      </div>

      {showSynthesis ? (
        <>
          <p className="mt-0.5 text-[11.5px] leading-snug text-[var(--color-text2)]">
            {item.synthesis}
          </p>
          {item.highlightedSources.length > 0 && (
            <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
              {item.highlightedSources.map((s) => (
                <li
                  key={s.url}
                  className="text-[10px] text-[var(--color-text3)]"
                >
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 hover:text-[var(--color-text2)] hover:underline"
                  >
                    {s.sourceName}
                    <ExternalLink className="h-2.5 w-2.5" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <ul className="mt-0.5 space-y-0.5">
          {item.topArticles.slice(0, 3).map((a) => (
            <li
              key={a.sourceUrl}
              className="text-[11px] text-[var(--color-text2)]"
            >
              <a
                href={a.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--color-text)] hover:underline"
              >
                {a.title}
              </a>
              <span className="ml-1.5 text-[9.5px] text-[var(--color-text3)]">
                {a.sourceName}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
